import Ajv from 'ajv'
import { Table } from '../models/Table'
import { TableSchema } from '../models/TableSchema'
import { TablesService } from './TablesService'
import { destructureWithOmit, recursiveConvertSnakeToCamelCase } from '@/utils'
import { TablesTypeGuardService } from './TablesTypeGuardService'
import { IQueueExecutionRequest } from '@/shared/Executions/models/IQueueExecutionRequest'
import { CdnSchemaJsonParsingResult } from '@/shared/JsonEditorSchema/models/CdnSchemaJsonParsingResult'
import { SchemaJsonValidationService } from '@/shared/JsonEditorSchema/services/SchemaJsonValidationService'


export class TablesJsonParsingResult extends CdnSchemaJsonParsingResult<Table, TablesJsonParsingInputType> {

    public name: string = "";

    constructor(
        valid: boolean,
        parsedValue?: any,
        errors: Array<string> = []
    ) {
        super(valid, parsedValue, errors);
        if (valid && parsedValue) {
            if ('customTables' in parsedValue && parsedValue.customTables?.tables) {
                this.parsedEntities = parsedValue.customTables.tables;
                this.name = parsedValue?.name;
                this.inputType = 'full';
            } else if ('tables' in parsedValue && Array.isArray(parsedValue.tables)) {
                this.parsedEntities = parsedValue.tables;
                this.name = parsedValue?.name;
                this.inputType = 'flat';
            } else if (Array.isArray(parsedValue)) {
                this.parsedEntities = parsedValue;
                this.inputType = 'array';
            }
        }
    }

    public withParsedTables(tables: Table[]): this {
        this.parsedEntities = tables;
        return this;
    }
}

interface IFlatTablesArrayStructure {
    tables: Table[];
}

type ITablesArray = Table[];

export type TablesJsonParsingInputType = 'full' | 'flat' | 'array';

export class TablesJsonParsingService extends SchemaJsonValidationService<Table, TablesJsonParsingInputType, TableSchema> {

    static readonly injectableKey = 'TablesJsonParsingService';

    get injectableKey() {
        return (this.constructor as typeof TablesJsonParsingService).injectableKey;
    }

    //@ts-ignore
    public initializeValidators(defaultType?: string): void {
        //noop
    }

    private _fullTablesPayloadStructureSchemaValidator = new Ajv().compile<IQueueExecutionRequest>({
        type: 'object',
        properties: {
            customTables: {
                type: 'object',
                properties: {
                    tables: {
                        type: 'array',
                        items: {
                            type: 'object',
                            properties: {
                                type: { type: 'string' }
                            },
                            required: ['type'],
                            additionalProperties: true
                        }
                    }
                },
                required: ['tables'],
                additionalProperties: true
            }
        },
        required: ['customTables'],
        additionalProperties: true
    });

    private _flatTablesArrayStructureSchemaValidator = new Ajv().compile<IFlatTablesArrayStructure>({
        type: 'object',
        properties: {
            tables: {
                type: 'array',
                items: {
                    type: 'object',
                    properties: {
                        type: { type: 'string' }
                    },
                    required: ['type'],
                    additionalProperties: true
                }
            }
        },
        required: ['tables'],
        additionalProperties: true
    });

    private _tablesArraySchemaValidator = new Ajv().compile<ITablesArray>({
        type: 'array',
        items: {
            type: 'object',
            properties: {
                type: { type: 'string' }
            },
            required: ['type'],
            additionalProperties: true
        }
    });

    public tryParseEntitiesFromJson(value: string, tableSchemas: Array<TableSchema>): TablesJsonParsingResult {
        if (tableSchemas == null || tableSchemas.length <= 0) throw new Error("tableSchemas is required");

        var result = new TablesJsonParsingResult(true);

        if (value == null) {
            result.valid = false;
            result.errors.push("Cannot parse JSON from null or empty input value.");
            return result;
        }
        try {
            result.parsedValue = JSON.parse(value);
            recursiveConvertSnakeToCamelCase(result.parsedValue)
            return this.tryParseTables(result, tableSchemas);
        }
        catch (error: any) {
            result.valid = false;
            result.errors.push(error);
        }

        return result;
    }

    private tryParseTables(result: TablesJsonParsingResult, tableSchemas: Array<TableSchema>): TablesJsonParsingResult {

        if (result.parsedValue == null) throw new Error("Invalid tables.parseValue encountered trying to validateTableStructure.");

        const parsed = result.parsedValue;
        let tables: Table[] | undefined;

        // reset errors from any prior validation
        this._fullTablesPayloadStructureSchemaValidator.errors = null;
        this._flatTablesArrayStructureSchemaValidator.errors = null;
        this._tablesArraySchemaValidator.errors = null;

        if (this._fullTablesPayloadStructureSchemaValidator(parsed)) {
            tables = parsed.customTables.tables;
        } else if (this._flatTablesArrayStructureSchemaValidator(parsed)) {
            tables = parsed.tables;
        } else if (this._tablesArraySchemaValidator(parsed)) {
            tables = parsed;
        } else {
            const errors = [
                ...(this._fullTablesPayloadStructureSchemaValidator.errors || []),
                ...(this._flatTablesArrayStructureSchemaValidator.errors || []),
                ...(this._tablesArraySchemaValidator.errors || [])
            ]
                .map((e: any) => `${e.instancePath} ${e.message}`)
                .slice(0, 5);

            return new TablesJsonParsingResult(false, undefined, errors);
        }

        try {
            const initialized = tables.map(x => this.initializeEntityWithConfiguration(x, tableSchemas));
            return new TablesJsonParsingResult(true, parsed).withParsedTables(initialized);
        } catch (err: any) {
            return new TablesJsonParsingResult(false, parsed, [`${err.message}`]);
        }
    }

    public initializeEntityWithConfiguration(table: Table, tableSchemas: Array<TableSchema>): Table {
        if (table == null) throw new Error("table is required");
        if (!table.type) throw new Error("table.type is required")
        return this.initializeEntity(table, tableSchemas, 1);
    }

    public initializeEntity(table: Table, tableSchemas: Array<TableSchema>, configurationLevel: number): Table {
        let initializedTable = table;
        const tableSchema = TablesService.tryGetTableSchemaByType(table.type, tableSchemas);
        if (tableSchema != null) {
            initializedTable = TablesService.createTableFromTableSchema(tableSchema);
            if (table.description != null) initializedTable.description = table.description;
            initializedTable._type = table.type;
            initializedTable.configuration = destructureWithOmit(table, []);
            initializedTable.configuration._type = table.type;

            // Special-case for rule comprised of rules (E.g. Data Grouping) or RuleSchema with payload_template that may have groups 
            const tableRequiresRecursiveInitialization = this.tableRequiresRecursiveInitialization(table);
            //TODO: evaluate payload_template itself for groups instead of just disabling
            const tableSchemaRequiresRecursiveInitialization = this.tableSchemaRequiresRecursiveInitialization(tableSchema);

            if (configurationLevel > 1 &&
                (tableRequiresRecursiveInitialization || tableSchemaRequiresRecursiveInitialization)) {
                throw new Error("Non top-level tables that contain tables not supported.");
            }
            else if (tableRequiresRecursiveInitialization) {
                throw new Error("Tables containing child tables is not supported.");
                /*
                const initializedSubTables = new Array<Table>();
                (initializedTable.configuration?.tables || []).forEach((t: Table) => {
                    initializedSubTables.push(this.initializeTable(t, tableSchemas, configurationLevel + 1))
                });
                initializedTable.configuration.tables = initializedSubTables;
                */
            }

        }
        return initializedTable;
    }

    private tableRequiresRecursiveInitialization(table: Table): boolean {
        return TablesTypeGuardService.supportsChildTables(table);
    }

    private tableSchemaRequiresRecursiveInitialization(tableSchema: TableSchema): boolean {
        return TablesTypeGuardService.supportsChildTables(tableSchema);
    }
}
